import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { getAllPurposes } from "../../services/purposes";
import { createSubmission } from "../../services/submissions";
import { CustomSelect } from "./CustomSelect";
import FormActions from "./FormActions";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { getMyData } from "../../services/myData";
import { Badge } from "./Badge";
import { Button } from "./Button";

const normalizeKey = (str) =>
  str
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "") ?? "";

// My Data's attribute names ("Email") and a purpose's dataCategories ("Email
// Address") refer to the same category but aren't identical strings, so an
// exact normalizeKey match misses real matches — fall back to substring
// containment either direction.
const categoriesMatch = (a, b) => {
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
};

const PURPOSE_PAGE_SIZE = 25;

// Record metadata, not PII the user can request changes to — never offer
// these as data categories.
const NON_EDITABLE_ATTRIBUTE_KEYS = new Set([
  "id",
  "createdat",
  "updatedat",
  "numrecid",
  "numericid",
]);
const isEditableAttribute = (name) =>
  !NON_EDITABLE_ATTRIBUTE_KEYS.has(normalizeKey(name));

// Data category names are free-text from the backend, so rules are matched
// by keyword against the normalized category name rather than an exact enum.
const CATEGORY_VALIDATION_RULES = [
  {
    keywords: ["email"],
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    hint: "e.g. name@example.com",
    error: "Please enter a valid email address.",
    inputType: "email",
  },
  {
    keywords: ["phone", "mobile", "contactnumber"],
    pattern: /^[6-9]\d{9}$/,
    hint: "10-digit mobile number",
    error: "Please enter a valid 10-digit mobile number.",
    inputType: "tel",
  },
  {
    keywords: ["dateofbirth", "dob", "birthdate"],
    pattern: /^\d{4}-\d{2}-\d{2}$/,
    hint: "YYYY-MM-DD",
    error: "Please enter a valid date in YYYY-MM-DD format.",
    inputType: "date",
  },
  {
    keywords: ["pan"],
    pattern: /^[A-Z]{5}\d{4}[A-Z]$/,
    hint: "e.g. ABCDE1234F",
    error: "Please enter a valid PAN (e.g. ABCDE1234F).",
    inputType: "text",
    transform: (v) => v.toUpperCase(),
  },
  {
    keywords: ["aadhaar", "aadhar"],
    pattern: /^\d{12}$/,
    hint: "12-digit Aadhaar number",
    error: "Please enter a valid 12-digit Aadhaar number.",
    inputType: "text",
  },
  {
    keywords: ["pincode", "postalcode", "zipcode", "zip"],
    pattern: /^\d{6}$/,
    hint: "6-digit pincode",
    error: "Please enter a valid 6-digit pincode.",
    inputType: "text",
  },
];

const getValidationRule = (category) => {
  const key = normalizeKey(category);
  return CATEGORY_VALIDATION_RULES.find((rule) =>
    rule.keywords.some((kw) => key.includes(kw)),
  );
};

const humanizeAttribute = (str) =>
  str
    ?.split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") ?? str;

export default function SubmitRequestModal({ tenantId, onClose, onSuccess }) {
  const isMobile = useIsMobile();
  // Purposes are shown as informational tags at the bottom of the modal only —
  // the request/erasure itself is no longer scoped to a single purpose.
  const [purposes, setPurposes] = useState([]);
  const [purposePage, setPurposePage] = useState(1);
  const [purposeHasMore, setPurposeHasMore] = useState(true);
  const [purposeLoading, setPurposeLoading] = useState(false);
  const [requestType, setRequestType] = useState("update");
  const [dataValues, setDataValues] = useState([]);
  const [fields, setFields] = useState([
    { field_name: "", old_value: "", new_value: "" },
  ]);
  const [erasureCategories, setErasureCategories] = useState([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataValuesLoading, setDataValuesLoading] = useState(true);

  const loadPurposes = useCallback(
    (page) => {
      if (!tenantId) return;
      setPurposeLoading(true);
      getAllPurposes(tenantId, { page, page_size: PURPOSE_PAGE_SIZE })
        .then((res) => {
          const data = res.data?.data || {};
          const items = data.items || [];
          setPurposes((prev) => {
            const merged = page === 1 ? items : [...prev, ...items];
            const seen = new Set();
            return merged.filter((p) => {
              const id = p.purpose_id ?? p.purposeId ?? p.id;
              if (id == null) return true;
              if (seen.has(id)) return false;
              seen.add(id);
              return true;
            });
          });
          setPurposeHasMore(page < (data.total_pages || 0));
          setPurposePage(page);
        })
        .catch(() => {
          if (page === 1) setPurposes([]);
          setPurposeHasMore(false);
        })
        .finally(() => setPurposeLoading(false));
    },
    [tenantId],
  );

  useEffect(() => {
    loadPurposes(1);
  }, [loadPurposes]);

  // The Data Category dropdown must reflect every purpose's dataCategories,
  // not just the first page, so keep fetching subsequent pages in the
  // background until exhausted rather than waiting on a manual click.
  useEffect(() => {
    if (purposePage > 0 && purposeHasMore && !purposeLoading) {
      loadPurposes(purposePage + 1);
    }
  }, [purposePage, purposeHasMore, purposeLoading, loadPurposes]);

  // Tenant-wide data values from the My Data API, used both as the source of
  // available data categories and to prefill "Current Value" once one is
  // picked.
  useEffect(() => {
    if (!tenantId) return;
    setDataValuesLoading(true);
    getMyData(tenantId)
      .then((res) => {
        const payload = res.data?.data ?? res.data?.fields ?? res.data;
        setDataValues(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setDataValues([]))
      .finally(() => setDataValuesLoading(false));
  }, [tenantId]);

  const lookupValue = (category) => {
    const key = normalizeKey(category);
    const match = dataValues.find(
      (item) => normalizeKey(item.field ?? item.attribute) === key,
    );
    return match?.value ?? "";
  };

  // Data categories offered in the dropdown come from the My Data API —
  // the actual attributes this tenant holds on the user.
  const availableCategories = Array.from(
    new Set(
      dataValues
        .map((item) => item.field ?? item.attribute)
        .filter(Boolean)
        .filter(isEditableAttribute),
    ),
  );

  const addField = () =>
    setFields((prev) => [
      ...prev,
      { field_name: "", old_value: "", new_value: "" },
    ]);

  // Once every available category is already used in some row, there's
  // nothing left to add — hide/disable the add button instead of letting
  // users create a row that can only duplicate an existing selection.
  const canAddField =
    availableCategories.length > 0 && fields.length < availableCategories.length;

  const removeField = (idx) =>
    setFields((prev) => prev.filter((_, i) => i !== idx));

  const updateField = (idx, key, value) =>
    setFields((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, [key]: value } : f)),
    );

  const toggleErasureCategory = (category) =>
    setErasureCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );

  // Affected Purposes stays hidden until the user has picked at least one
  // data category, then shows only the purposes whose dataCategories include it.
  const selectedCategories =
    requestType === "erasure"
      ? erasureCategories
      : fields.map((f) => f.field_name).filter(Boolean);
  const hasSelectedCategory = selectedCategories.length > 0;
  const selectedCategoryKeys = selectedCategories.map(normalizeKey);
  const affectedPurposes = purposes.filter((p) =>
    (p.dataCategories ?? p.data_categories ?? []).some((c) => {
      const key = normalizeKey(c);
      return selectedCategoryKeys.some((selectedKey) =>
        categoriesMatch(key, selectedKey),
      );
    }),
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requestType === "erasure" && erasureCategories.length === 0) {
      toast.error("Please select at least one data category to erase.");
      return;
    }
    if (requestType === "update" && fields.some((f) => !f.field_name)) {
      toast.error("Please select a data category for every row.");
      return;
    }
    if (requestType === "update" && fields.some((f) => !f.new_value.trim())) {
      toast.error("Updated Data is required.");
      return;
    }
    if (
      requestType === "update" &&
      fields.some(
        (f) =>
          f.new_value.trim() &&
          f.new_value.trim() === (f.old_value ?? "").trim(),
      )
    ) {
      toast.error("Updated Value must be different from the Current Value.");
      return;
    }
    if (
      requestType === "update" &&
      fields.some((f) => {
        const rule = getValidationRule(f.field_name);
        return rule && !rule.pattern.test(f.new_value.trim());
      })
    ) {
      toast.error("Please fix the invalid values before submitting.");
      return;
    }
    setLoading(true);
    try {
      const submission_data_filed =
        requestType === "erasure"
          ? erasureCategories.map((category) => ({
              field_name: category,
              old_value: lookupValue(category),
              new_value: "",
            }))
          : fields;
      await createSubmission(tenantId, undefined, {
        submission_type: requestType === "erasure" ? "erasure" : "request",
        description,
        submission_data_filed,
      });
      toast.success("Request submitted successfully!");
      onSuccess?.();
      onClose();
    } catch {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 md:flex md:items-center md:justify-center z-50 md:p-4">
      <div className="bg-white md:rounded-2xl shadow-xl w-full h-full md:h-auto md:max-w-2xl md:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 bg-gray-50 md:bg-white sticky top-0 md:static">
          {isMobile ? (
            <Button
              variant="tertiary"
              onClick={onClose}
              className="!text-gray-700 !p-1 -ml-1"
              aria-label="Back"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </Button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">
              Submit a Request
            </h2>
          )}
          {isMobile && (
            <span className="text-base font-semibold text-gray-800">
              Submit a Request
            </span>
          )}
          {isMobile ? (
            <span className="w-[22px]" />
          ) : (
            <Button
              variant="tertiary"
              onClick={onClose}
              className="!text-gray-400 hover:!text-gray-600"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Button>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 md:p-6 pb-24 md:pb-6 space-y-5"
        >
          {/* Request Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Request Type
            </label>
            <div className="hidden md:flex gap-8">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="request_type"
                  checked={requestType === "update"}
                  onChange={() => setRequestType("update")}
                  className="w-4 h-4 accent-teal-600"
                />
                <span className="text-sm text-gray-700">Update</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="request_type"
                  checked={requestType === "erasure"}
                  onChange={() => setRequestType("erasure")}
                  className="w-4 h-4 accent-teal-600"
                />
                <span className="text-sm text-gray-700">Erasure</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3 md:hidden">
              {[
                { value: "update", label: "Update" },
                { value: "erasure", label: "Erasure" },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2 border rounded-lg px-3 py-2.5 cursor-pointer ${
                    requestType === opt.value
                      ? "border-teal-500 bg-teal-50/40"
                      : "border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="request_type_mobile"
                    checked={requestType === opt.value}
                    onChange={() => setRequestType(opt.value)}
                    className="w-4 h-4 accent-teal-600"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 my-1">
              Describe your request
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder={
                requestType === "erasure"
                  ? "e.g. I want to erase my phone number. Please erase it."
                  : "e.g. My phone number has changed to +91 99999 00000, please update it"
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
            />
          </div>

          {/* Erasure: data categories currently in use */}
          {requestType === "erasure" && (
            <div className="pt-2 border-t border-gray-100">
              <span className="block text-sm font-medium text-gray-700 mb-3 mt-4">
                Select Data for Erasure
              </span>
              {dataValuesLoading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : availableCategories.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No data categories available.
                </p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availableCategories.map((category) => (
                    <label
                      key={category}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={erasureCategories.includes(category)}
                        onChange={() => toggleErasureCategory(category)}
                        className="w-4 h-4 rounded accent-teal-600"
                      />
                      <span className="text-sm text-gray-700">
                        {humanizeAttribute(category)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Data Fields */}
          {requestType === "update" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Select Data Fields
                </span>
                {canAddField && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addField}
                    leftIcon={
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    }
                    className="hidden md:inline-flex !px-3 !py-1.5"
                  >
                    Data Category
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {fields.map((field, idx) => {
                  const rule = getValidationRule(field.field_name);
                  const trimmedValue = field.new_value.trim();
                  const isUnchanged =
                    !!trimmedValue &&
                    trimmedValue === (field.old_value ?? "").trim();
                  const isInvalid =
                    isUnchanged ||
                    (!!rule && !!trimmedValue && !rule.pattern.test(trimmedValue));
                  const errorMessage = isUnchanged
                    ? "Updated Value must be different from the Current Value."
                    : rule?.error;
                  // Exclude categories already picked in other rows so the
                  // same Data Category can't be selected twice.
                  const rowOptions = availableCategories.filter(
                    (t) =>
                      t === field.field_name ||
                      !fields.some(
                        (f, i) => i !== idx && f.field_name === t,
                      ),
                  );
                  const renderCategorySelect = () => (
                    <CustomSelect
                      value={field.field_name}
                      onChange={(category) => {
                        setFields((prev) =>
                          prev.map((f, i) =>
                            i === idx
                              ? {
                                  ...f,
                                  field_name: category,
                                  old_value: lookupValue(category),
                                  new_value: "",
                                }
                              : f,
                          ),
                        );
                      }}
                      placeholder={
                        purposeLoading && purposes.length === 0
                          ? "Loading..."
                          : rowOptions.length === 0
                            ? "No data categories available"
                            : "Select data category"
                      }
                      buttonClassName="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex items-center justify-between"
                      options={rowOptions.map((t) => ({
                        value: t,
                        label: humanizeAttribute(t),
                      }))}
                    />
                  );
                  const renderUpdatedValueInput = () => (
                    <input
                      type={rule?.inputType ?? "text"}
                      required
                      value={field.new_value}
                      onChange={(e) => {
                        const raw = e.target.value;
                        updateField(
                          idx,
                          "new_value",
                          rule?.transform ? rule.transform(raw) : raw,
                        );
                      }}
                      className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                        isInvalid
                          ? "border-[#C73838] focus:ring-red-400"
                          : "border-gray-300 focus:ring-teal-500"
                      }`}
                      placeholder={rule?.hint ?? "New value"}
                    />
                  );
                  const renderDeleteIcon = () => (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14H6L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4h6v2" />
                    </svg>
                  );
                  return (
                    <div key={idx}>
                      {/* Desktop layout */}
                      <div className="hidden md:grid grid-cols-3 gap-3 items-start">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Data Category
                          </label>
                          {renderCategorySelect()}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">
                            Current Value
                          </label>
                          <input
                            type="text"
                            value={field.old_value}
                            readOnly
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none"
                            placeholder="Current value"
                          />
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-500 mb-1">
                              Updated Value{" "}
                              <span className="text-[#C73838]">*</span>
                            </label>
                            {renderUpdatedValueInput()}
                            {isInvalid && (
                              <p className="mt-1 text-xs text-[#C73838]">
                                {errorMessage}
                              </p>
                            )}
                          </div>
                          {fields.length > 1 && (
                            <Button
                              type="button"
                              variant="tertiary"
                              onClick={() => removeField(idx)}
                              className="mt-5 !p-0 !text-red-400 hover:!text-red-600"
                            >
                              {renderDeleteIcon()}
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Mobile layout */}
                      <div className="md:hidden bg-[#F9F8FF] rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <div className="flex-1">{renderCategorySelect()}</div>
                          {fields.length > 1 && (
                            <Button
                              type="button"
                              variant="tertiary"
                              onClick={() => removeField(idx)}
                              className="shrink-0 !p-2.5 border border-red-300 !text-[#C73838] hover:!bg-red-50"
                              aria-label="Remove field"
                            >
                              {renderDeleteIcon()}
                            </Button>
                          )}
                        </div>
                        {field.old_value && (
                          <>
                            <div className="text-sm text-gray-600 mt-3">
                              Current Value :{" "}
                              <span className="font-medium text-gray-800">
                                {field.old_value}
                              </span>
                            </div>
                            <div className="border-t border-gray-200 my-3" />
                          </>
                        )}
                        <div className={field.old_value ? "" : "mt-3"}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Updated Value{" "}
                            <span className="text-[#C73838]">*</span>
                          </label>
                          {renderUpdatedValueInput()}
                          {isInvalid && (
                            <p className="mt-1 text-xs text-[#C73838]">
                              {errorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {canAddField && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={addField}
                  leftIcon={
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  }
                  className="md:hidden mt-3 w-full !border-dashed !py-3 !text-[#0F4C5C]"
                >
                  Add Data Field
                </Button>
              )}
            </div>
          )}

          {/* Purposes — informational only, not required, not sent in the payload.
              Hidden until a data category is selected; shown list is scoped to
              purposes whose dataCategories include the selected category(ies). */}
          {hasSelectedCategory && (
            <div className="pt-4 border-t border-gray-100">
              <span className="block text-sm font-medium text-gray-700 mb-3">
                Affected Purposes
              </span>
              {affectedPurposes.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No purposes found for the selected data category.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {affectedPurposes.map((p) => {
                    const id = p.purpose_id ?? p.purposeId ?? p.id;
                    const label = p.purpose ?? p.name;
                    return <Badge key={id} value={label} variant="neutral" />;
                  })}
                </div>
              )}
              {purposeHasMore && (
                <p className="mt-2 text-xs text-gray-400">
                  Loading more purposes...
                </p>
              )}
            </div>
          )}

          <FormActions
            onCancel={onClose}
            loading={loading}
            submitLabel="Submit Request"
            loadingLabel="Submitting..."
            submitDisabled={
              loading ||
              (requestType === "erasure" && erasureCategories.length === 0) ||
              (requestType === "update" &&
                fields.some((f) => {
                  const trimmedValue = f.new_value.trim();
                  if (!trimmedValue) return true;
                  if (trimmedValue === (f.old_value ?? "").trim()) return true;
                  const rule = getValidationRule(f.field_name);
                  return rule && !rule.pattern.test(trimmedValue);
                }))
            }
          />
        </form>
      </div>
    </div>
  );
}
